import { useState } from 'react';
import {
    ArrowBack as BackIcon,
    RocketLaunchOutlined as LaunchIcon,
    SaveOutlined as SaveIcon,
} from '@mui/icons-material';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useCreateAutomation, useUpdateAutomation } from '@/hooks/queries/dm-automation';
import type { DmAutomationDto } from '@/types/dmAutomation';
import { ACCENT, ACCENT_HOVER, PageHeader } from './_shared';
import Step1Trigger from './wizard/Step1Trigger';
import Step2Name from './wizard/Step2Name';
import Step3Keywords from './wizard/Step3Keywords';
import Step4Target from './wizard/Step4Target';
import Step5Message from './wizard/Step5Message';
import {
    EMPTY_WIZARD_DATA,
    buildCreatePayload,
    buildUpdatePayload,
    dtoToWizardData,
    isStepApplicable,
    validateStep,
    type WizardData,
    type WizardStep,
} from './wizard/wizardTypes';

interface Props {
    automation?: DmAutomationDto;
    onExit: () => void;
}

interface SectionProps {
    num: number;
    title: string;
    children: React.ReactNode;
}

const Section = ({ num, title, children }: SectionProps) => (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: ACCENT,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                    }}
                >
                    {num}
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
            </Stack>
            {children}
        </CardContent>
    </Card>
);

const AutomationFormPage = ({ automation, onExit }: Props) => {
    const isEditMode = Boolean(automation);
    const [data, setData] = useState<WizardData>(() =>
        automation ? dtoToWizardData(automation) : EMPTY_WIZARD_DATA,
    );
    const create = useCreateAutomation();
    const update = useUpdateAutomation();
    const isPending = create.isPending || update.isPending;

    const updateData = (patch: Partial<WizardData>) => setData((prev) => ({ ...prev, ...patch }));

    const handleSubmit = async () => {
        for (let s = 1 as WizardStep; s <= 5; s = (s + 1) as WizardStep) {
            if (!isStepApplicable(s, data.triggerType)) continue;
            const err = validateStep(s, data);
            if (err) {
                toast.warning(err);
                return;
            }
        }
        try {
            if (automation) {
                await update.mutateAsync({ id: automation.id, body: buildUpdatePayload(data) });
                toast.success('자동화를 수정했어요');
            } else {
                await create.mutateAsync(buildCreatePayload(data));
                toast.success('자동화가 시작됐어요 🚀');
            }
            onExit();
        } catch (e: any) {
            toast.error(e?.data?.message || (isEditMode ? '자동화 수정에 실패했어요' : '자동화 생성에 실패했어요'));
        }
    };

    // 적용 가능한 섹션을 순서대로 모으되 동적 번호 부여
    let n = 0;
    const sections: { key: string; num: number; title: string; node: React.ReactNode }[] = [];
    sections.push({
        key: 'trigger',
        num: ++n,
        title: '트리거',
        node: <Step1Trigger compact data={data} onChange={updateData} />,
    });
    // 신규 생성 시 트리거 미선택이면 이후 섹션 숨김 (사용자 흐름 가이드)
    const hasTrigger = data.triggerType !== '';
    if (hasTrigger) {
        sections.push({
            key: 'name',
            num: ++n,
            title: '이름',
            node: <Step2Name compact data={data} onChange={updateData} />,
        });
        if (isStepApplicable(3, data.triggerType)) {
            sections.push({
                key: 'keywords',
                num: ++n,
                title: data.triggerType === 'STORY_REPLY' ? '키워드 (선택)' : '키워드',
                node: <Step3Keywords compact data={data} onChange={updateData} />,
            });
        }
        if (isStepApplicable(4, data.triggerType)) {
            sections.push({
                key: 'target',
                num: ++n,
                title: '대상 게시물',
                node: <Step4Target compact data={data} onChange={updateData} />,
            });
        }
        sections.push({
            key: 'message',
            num: ++n,
            title: '메시지',
            node: <Step5Message compact data={data} onChange={updateData} />,
        });
    }

    return (
        <Box>
            <PageHeader
                title={isEditMode ? '자동화 수정' : '새 자동화 만들기'}
                desc={
                    isEditMode
                        ? '수정할 항목을 자유롭게 바꾸고 하단의 저장을 눌러주세요'
                        : '아래 항목을 모두 채우고 활성화하면 자동으로 DM이 발송돼요'
                }
                action={
                    <Button
                        onClick={onExit}
                        startIcon={<BackIcon />}
                        variant="outlined"
                        disabled={isPending}
                        sx={{ textTransform: 'none', cursor: 'pointer', borderRadius: 2 }}
                    >
                        목록으로
                    </Button>
                }
            />

            <Stack spacing={2}>
                {sections.map((s) => (
                    <Section key={s.key} num={s.num} title={s.title}>
                        {s.node}
                    </Section>
                ))}

                {!hasTrigger && (
                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                        먼저 트리거를 선택하면 나머지 항목이 나타나요.
                    </Alert>
                )}
            </Stack>

            <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1.5}
                sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}
            >
                <Button
                    onClick={onExit}
                    variant="outlined"
                    disabled={isPending}
                    sx={{ textTransform: 'none', borderRadius: 2, cursor: 'pointer' }}
                >
                    취소
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isPending || !hasTrigger}
                    startIcon={
                        isPending ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : isEditMode ? (
                            <SaveIcon />
                        ) : (
                            <LaunchIcon />
                        )
                    }
                    disableElevation
                    sx={{
                        bgcolor: ACCENT,
                        color: '#fff',
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 2.5,
                        cursor: 'pointer',
                        transition: 'background-color 120ms ease, transform 120ms ease',
                        willChange: 'transform',
                        '&:hover': { bgcolor: ACCENT_HOVER, transform: 'translateY(-1px)' },
                        '&:active': { transform: 'translateY(0)' },
                        '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                    }}
                >
                    {isEditMode ? '저장' : '활성화하고 시작'}
                </Button>
            </Stack>
        </Box>
    );
};

export default AutomationFormPage;
